import * as React from "react";

import { useRouter } from "next/router";

import { PermissionContext } from "context/permissioncontext";
import { UserContext } from "context/usercontext";
import {
  DOWNLOAD_PERMISSION,
  ORG_ACCOUNT_INFO_PATH,
  ORG_AWS_CREDENTIALS_PATH,
  ORG_AWS_NETWORK_PATH,
  ORG_BLUEPRINTS_PATH,
  ORG_CONTACT_PATH,
  ORG_DISKS_PATH,
  ORG_INSTANCE_TYPES_PATH,
  ORG_LICENSES_PATH,
  ORG_USER_GROUPS_PATH,
  ORG_USERS_PATH,
  ORG_WORKFLOW_CONFIG_PATH,
  ORG_WORKFLOW_TEMPLATES_PATH,
  ORG_WORKFLOWS_PATH,
  ORG_FEATURES_PATH,
} from "src/lib/const";

// Here we are gathering the protected routes based on admin type
// This is recalculated on navigation and/or user change
const perms = new Set<string>();
const getPerms = (user: MyUser) => {
  perms.clear();
  if (user && (user.is_org_admin || user.is_user_admin || user.is_tech_admin)) {
    perms.add(DOWNLOAD_PERMISSION);
    perms.add(ORG_WORKFLOWS_PATH);
    perms.add(ORG_ACCOUNT_INFO_PATH);
    if (user.is_org_admin) {
      perms.add(ORG_USERS_PATH);
      perms.add(ORG_DISKS_PATH);
      perms.add(ORG_INSTANCE_TYPES_PATH);
      perms.add(ORG_BLUEPRINTS_PATH);
      perms.add(ORG_USER_GROUPS_PATH);
      perms.add(ORG_CONTACT_PATH);
      perms.add(ORG_WORKFLOW_CONFIG_PATH);
      perms.add(ORG_AWS_CREDENTIALS_PATH);
      perms.add(ORG_AWS_NETWORK_PATH);
      perms.add(ORG_LICENSES_PATH);
      perms.add(ORG_WORKFLOW_TEMPLATES_PATH);
      perms.add(ORG_FEATURES_PATH);
    }
    if (user.is_tech_admin) {
      perms.add(ORG_CONTACT_PATH);
      perms.add(ORG_WORKFLOW_CONFIG_PATH);
      perms.add(ORG_AWS_CREDENTIALS_PATH);
      perms.add(ORG_AWS_NETWORK_PATH);
      perms.add(ORG_LICENSES_PATH);
    }
    if (user.is_user_admin) {
      perms.add(ORG_USERS_PATH);
      perms.add(ORG_DISKS_PATH);
      perms.add(ORG_INSTANCE_TYPES_PATH);
      perms.add(ORG_BLUEPRINTS_PATH);
      perms.add(ORG_USER_GROUPS_PATH);
      perms.add(ORG_FEATURES_PATH);
      perms.add(ORG_WORKFLOW_TEMPLATES_PATH);
    }
  }
};

const MyPermissions = ({ children }) => {
  const router = useRouter();
  const user = React.useContext(UserContext);

  // authed is a memoized boolean that is recalculated every navigation or user change
  // this recalculates the permissions which is then passed down to child components via context
  const authed = React.useMemo((): boolean => {
    if (user) {
      getPerms(user);
      if (router.pathname.includes("/organization")) {
        return perms.has(router.pathname);
      } else {
        return true;
      }
    }
    return false;
  }, [user, router.pathname]);

  // No user do nothing
  if (!user) {
    return null;
  }

  // User and Authorized pass context down
  if (authed) {
    return <PermissionContext.Provider value={perms}>{children}</PermissionContext.Provider>;
  }

  // User and Unauthorized, forward to 403
  router.push("/403");
  return null;
};

export default MyPermissions;
